import { useCallback, useSyncExternalStore } from 'react';

/**
 * Theme preference: 'light', 'dark' or 'system' (follow the OS).
 *
 * The RESOLVED theme is written to `<html data-theme="…">`, which is the only
 * thing styles/tokens.css keys off. public/index.html runs the same resolution
 * inline before the bundle loads, so a dark-mode user never sees a white flash
 * while React boots; this module takes over from there.
 *
 * A module-level store rather than component state: the toggle lives in the
 * top bar, the user menu and the command palette at once, and three copies of
 * a useState would disagree the moment one of them changed it.
 *
 * One storage key, shared with that inline script. Change both or neither.
 */
export const THEME_STORAGE_KEY = 'ross-theme';
const PREFS = ['light', 'dark', 'system'];

const listeners = new Set();
const mq = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(prefers-color-scheme: dark)')
  : null;

function readPref() {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return PREFS.includes(v) ? v : 'system';
  } catch (_) {
    return 'system';
  }
}

function resolve(pref) {
  if (pref !== 'system') return pref;
  return mq && mq.matches ? 'dark' : 'light';
}

let state = { pref: readPref(), resolved: 'light' };

function apply() {
  const resolved = resolve(state.pref);
  state = { pref: state.pref, resolved };
  document.documentElement.dataset.theme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#0D0C13' : '#F6F5FA');
  listeners.forEach((l) => l());
}

apply();

if (mq) {
  const onOsChange = () => { if (state.pref === 'system') apply(); };
  if (mq.addEventListener) mq.addEventListener('change', onOsChange);
  else if (mq.addListener) mq.addListener(onOsChange);
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getSnapshot = () => state;

export function setThemePref(next) {
  if (!PREFS.includes(next)) return;
  try { localStorage.setItem(THEME_STORAGE_KEY, next); } catch (_) { /* ignore */ }
  state = { ...state, pref: next };
  apply();
}

export function useTheme() {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  // Cycles light -> dark -> system, which is what a single toggle button does.
  const cycle = useCallback(() => {
    setThemePref(PREFS[(PREFS.indexOf(snap.pref) + 1) % PREFS.length]);
  }, [snap.pref]);
  return { pref: snap.pref, resolved: snap.resolved, setPref: setThemePref, cycle };
}
